import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { SortDir } from '@/shared/hooks/useDataTable';

/**
 * Tabla genérica con cabecera ordenable + estados loading/empty. Presentacional
 * puro: el orden/paginación los maneja `useDataTable`. Reemplaza el `<thead>` +
 * `tbody` duplicado en los 5 modales (ver ANALISIS-CRITICO H3).
 */
export interface Column<T> {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  render: (item: T) => ReactNode;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (item: T) => string | number;
  sortKey?: string;
  sortDir?: SortDir;
  onSort?: (key: string) => void;
  onRowClick?: (item: T) => void;
  loading?: boolean;
  loadingText?: string;
  emptyText?: string;
}

const alignClass: Record<NonNullable<Column<unknown>['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  sortKey,
  sortDir,
  onSort,
  onRowClick,
  loading = false,
  loadingText = 'Cargando…',
  emptyText = 'Sin resultados para los filtros actuales.',
}: DataTableProps<T>) {
  if (loading) {
    return <div className="p-10 text-center text-sm text-slate-400">{loadingText}</div>;
  }
  if (rows.length === 0) {
    return <div className="p-10 text-center text-sm text-slate-400">{emptyText}</div>;
  }

  return (
    <table className="w-full border-collapse text-sm">
      <thead className="sticky top-0 z-10 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 shadow-sm">
        <tr>
          {columns.map((col) => {
            const active = sortKey === col.key;
            const canSort = col.sortable && onSort;
            return (
              <th
                key={col.key}
                className={`px-3 py-2 font-semibold ${alignClass[col.align ?? 'left']} ${
                  canSort ? 'cursor-pointer select-none hover:text-slate-700' : ''
                }`}
                onClick={canSort ? () => onSort?.(col.key) : undefined}
                aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                <span className="inline-flex items-center gap-1">
                  {col.header}
                  {active &&
                    (sortDir === 'asc' ? (
                      <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowDown className="h-3 w-3" />
                    ))}
                </span>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((item) => (
          <tr
            key={rowKey(item)}
            onClick={onRowClick ? () => onRowClick(item) : undefined}
            className={`border-t border-slate-100 ${
              onRowClick ? 'cursor-pointer hover:bg-slate-50' : ''
            }`}
          >
            {columns.map((col) => (
              <td key={col.key} className={`px-3 py-2 ${alignClass[col.align ?? 'left']}`}>
                {col.render(item)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
